import { arrayOf, func, object, string } from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactTable from 'react-table';
import { Button, Col, Row } from 'reactstrap';

import { getProductMetaFieldName } from '../../../campaign-editor/utils';
import BulkToggleControls from '../../../generic/BulkToggleControls';
import withConfirm from '../../../generic/with-confirm';
import { campaignProductMetaShape } from '../../../prop-types';
import {
  categoriesColumn,
  contentEntryNameColumn,
  contentIdColumn,
  countryCodesColumn,
  gendersColumn,
  productIdsColumn,
  productTagsColumn,
  selectContentId,
  thumbnail,
} from '../contentful-creatives-columns';

const DeleteButtonWithConfirm = withConfirm(
  'Do you really want to delete the selected content Ids and remove them from all campaign lines they are assigned to?',
)(Button);

function ContentfulCreativesTable({
  contentfulCreatives,
  campaignLineProducts,
  campaignProductTags,
  onUpdateContentfulCreatives,
  onChangeCampaign,
  campaignProductMetas,
  onDelete,
  onSave,
}) {
  const [transformedContentfulCreatives, setTransformedContentfulCreatives] =
    useState([]);

  const [selectedContentfulCreatives, setSelectedContentfulCreatives] =
    useState([]);

  useEffect(() => {
    const updated = contentfulCreatives.map(creative => {
      if (creative.productIds != null) return creative;

      const supported = campaignLineProducts.filter(product =>
        creative.supportedProductCodes?.includes(product.code),
      );
      return {
        ...creative,
        productIds: supported.map(p => p.value),
      };
    });
    setTransformedContentfulCreatives(updated);

    updated.forEach(updatedCreative => {
      if (updatedCreative.productIds != null) {
        onUpdateContentfulCreatives(updatedCreative);
      }
    });
  }, [contentfulCreatives, campaignLineProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const productMetaUpdates = new Map();

    transformedContentfulCreatives.forEach(creative => {
      creative.productIds?.forEach(prodId => {
        if (!productMetaUpdates.has(prodId)) {
          productMetaUpdates.set(prodId, new Set());
        }
        productMetaUpdates.get(prodId).add(creative.contentId);
      });
    });

    campaignProductMetas.forEach((productMeta, index) => {
      const expectedContentIds = Array.from(
        productMetaUpdates.get(productMeta.productId) ?? [],
      );
      const existingContentIds = productMeta.contentIds ?? [];
      const newContentIds = Array.from(new Set(expectedContentIds));

      const sortedNewContentIds = [...newContentIds].sort().join();
      const sortedExistingContentIds = [...existingContentIds].sort().join();
      if (sortedNewContentIds !== sortedExistingContentIds) {
        onChangeCampaign({
          target: {
            name: getProductMetaFieldName(index, 'contentIds'),
            value: newContentIds,
          },
        });
      }
    });
  }, [transformedContentfulCreatives, campaignProductMetas, onChangeCampaign]);

  const onChange = event => {
    const { name, value } = event.target;
    const [index, field] = name.split('.');

    setTransformedContentfulCreatives(prev =>
      prev.map((row, i) =>
        i === Number(index) ? { ...row, [field]: value } : row,
      ),
    );

    onUpdateContentfulCreatives({
      ...transformedContentfulCreatives[index],
      [field]: value,
    });
  };

  const handleToggleAll = checked => {
    setSelectedContentfulCreatives(
      checked ? contentfulCreatives.map(c => c.contentId) : [],
    );
  };

  const handleSelection = (id, e) => {
    const selected = e.target.checked;
    setSelectedContentfulCreatives(prev =>
      selected ? [...prev, id] : prev.filter(x => x !== id),
    );
  };

  const resetSelection = () => setSelectedContentfulCreatives([]);

  const handleDeleteSelectedContentfulCreatives = event => {
    selectedContentfulCreatives.forEach(id => onDelete(id));

    campaignProductMetas.forEach((meta, index) => {
      const updated = (meta.contentIds ?? []).filter(
        id => !selectedContentfulCreatives.includes(id),
      );
      onChangeCampaign({
        target: {
          name: getProductMetaFieldName(index, 'contentIds'),
          value: updated,
        },
      });
    });

    resetSelection();
    onSave(event);
  };

  const campaignProductTagsOptions = campaignProductTags.map(tag => ({
    label: tag,
    value: tag,
  }));

  const contentfulCreativesColumns = [
    selectContentId(handleSelection, selectedContentfulCreatives),
    contentEntryNameColumn,
    contentIdColumn,
    thumbnail,
    productIdsColumn(onChange, creative =>
      campaignLineProducts.filter(p =>
        creative.supportedProductCodes?.includes(p.code),
      ),
    ),
    productTagsColumn(onChange, campaignProductTagsOptions),
    gendersColumn,
    categoriesColumn(onChange),
    countryCodesColumn,
  ];

  const noCreativesSelected = selectedContentfulCreatives.length === 0;

  return (
    <>
      <Row className="mb-2">
        <Col>
          <BulkToggleControls
            entities={contentfulCreatives}
            activeEntities={selectedContentfulCreatives}
            onToggleAll={handleToggleAll}
          />
        </Col>
        <Col className="text-right">
          <DeleteButtonWithConfirm
            outline
            color="danger"
            size="sm"
            onClick={handleDeleteSelectedContentfulCreatives}
            className="mt-2"
            disabled={noCreativesSelected}
          >
            Delete selected
          </DeleteButtonWithConfirm>
        </Col>
      </Row>

      <ReactTable
        className="c-list-creatives small ReactTable--layout-card no-overflow"
        columns={contentfulCreativesColumns}
        data={transformedContentfulCreatives}
        minRows={1}
      />
    </>
  );
}

ContentfulCreativesTable.propTypes = {
  contentfulCreatives: arrayOf(object).isRequired, // eslint-disable-line react/forbid-prop-types,
  campaignLineProducts: arrayOf(string),
  campaignProductTags: arrayOf(string),
  onUpdateContentfulCreatives: func.isRequired,
  onChangeCampaign: func.isRequired,
  campaignProductMetas: arrayOf(campaignProductMetaShape).isRequired,
  onDelete: func.isRequired,
  onSave: func.isRequired,
};

ContentfulCreativesTable.defaultProps = {
  campaignProductTags: [],
  campaignLineProducts: [],
};

export default ContentfulCreativesTable;
